<script setup lang="ts">
import { ref, computed, watch, nextTick, onUnmounted, type Component } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useThemeStore } from '@/stores/theme'
import { apiGet } from '@/stores/api'
import {
  Search,
  Loader2,
  Ticket,
  Building2,
  Users,
  Monitor,
  FolderKanban,
  X,
  ArrowUp,
  ArrowDown,
  CornerDownLeft,
  Command,
  Plus,
  LayoutDashboard,
  CalendarDays,
  BookOpen,
  BarChart3,
  Shield,
  Sun,
  Moon,
  Clock,
  Hash,
  ChevronRight,
  Zap
} from 'lucide-vue-next'
import logger from '@/utils/logger'
import { useTabNavigation } from '@/composables/useTabNavigation'

const props = defineProps<{
  show: boolean
  tabMode?: boolean
}>()

const emit = defineEmits<{
  close: []
}>()

const router = useRouter()
const authStore = useAuthStore()
const themeStore = useThemeStore()
const { openInNewTab } = useTabNavigation()

/** Navigate or open in tab depending on tabMode */
function navigate(path: string, title?: string) {
  if (props.tabMode) {
    openInNewTab(path, title || path)
  } else {
    router.push(path)
  }
}

const query = ref('')
const searching = ref(false)
const activeFilter = ref('all')
const selectedIndex = ref(0)
const inputRef = ref<HTMLInputElement | null>(null)

// --- Recent searches (localStorage) ---
const RECENT_KEY = 'vhq_recent_searches'
const MAX_RECENT = 5

interface RecentSearch {
  query: string
  route: string
  label: string
  type: string
}

const recentSearches = ref<RecentSearch[]>([])

function loadRecent() {
  try {
    const stored = localStorage.getItem(RECENT_KEY)
    if (stored) recentSearches.value = JSON.parse(stored)
  } catch { /* ignore */ }
}

function saveRecent(item: RecentSearch) {
  // Dedupe by route
  recentSearches.value = [item, ...recentSearches.value.filter(r => r.route !== item.route)].slice(0, MAX_RECENT)
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(recentSearches.value))
  } catch { /* storage full */ }
}

function clearRecent() {
  recentSearches.value = []
  localStorage.removeItem(RECENT_KEY)
}

loadRecent()

// --- Command actions ---
interface CommandAction {
  id: string
  label: string
  description: string
  icon: Component
  iconColor: string
  handler: () => void
  permission?: string
  staffOnly?: boolean
  keywords: string[]
}

const allActions: CommandAction[] = [
  {
    id: 'new-ticket',
    label: 'Create New Ticket',
    description: 'Open ticket creation form',
    icon: Plus,
    iconColor: 'text-primary-400 bg-primary-500/10',
    handler: () => { navigate('/tickets?action=create', 'New Ticket') },
    permission: 'tickets.create',
    keywords: ['new', 'create', 'ticket', 'add'],
  },
  {
    id: 'go-dashboard',
    label: 'Go to Dashboard',
    description: 'Main dashboard',
    icon: LayoutDashboard,
    iconColor: 'text-emerald-400 bg-emerald-500/10',
    handler: () => { navigate('/', 'Dashboard') },
    keywords: ['dashboard', 'home', 'main'],
  },
  {
    id: 'go-tickets',
    label: 'Go to Tickets',
    description: 'View all tickets',
    icon: Ticket,
    iconColor: 'text-primary-400 bg-primary-500/10',
    handler: () => { navigate('/tickets', 'Tickets') },
    permission: 'tickets.view',
    keywords: ['tickets', 'support', 'helpdesk'],
  },
  {
    id: 'go-projects',
    label: 'Go to Projects',
    description: 'View all projects',
    icon: FolderKanban,
    iconColor: 'text-violet-400 bg-violet-500/10',
    handler: () => { navigate('/projects', 'Projects') },
    permission: 'projects.view',
    keywords: ['projects', 'kanban'],
  },
  {
    id: 'go-calendar',
    label: 'Go to Calendar',
    description: 'Schedule & events',
    icon: CalendarDays,
    iconColor: 'text-amber-400 bg-amber-500/10',
    handler: () => { navigate('/calendar', 'Calendar') },
    staffOnly: true,
    keywords: ['calendar', 'schedule', 'events'],
  },
  {
    id: 'go-kb',
    label: 'Go to Knowledge Base',
    description: 'Articles & documentation',
    icon: BookOpen,
    iconColor: 'text-cyan-400 bg-cyan-500/10',
    handler: () => { navigate('/knowledge-base', 'Knowledge Base') },
    keywords: ['knowledge', 'kb', 'articles', 'docs'],
  },
  {
    id: 'go-reports',
    label: 'Go to Reports',
    description: 'Analytics & reporting',
    icon: BarChart3,
    iconColor: 'text-violet-400 bg-violet-500/10',
    handler: () => { navigate('/reports', 'Reports') },
    permission: 'reports.view',
    staffOnly: true,
    keywords: ['reports', 'analytics', 'stats'],
  },
  {
    id: 'go-clients',
    label: 'Go to Clients',
    description: 'Client management',
    icon: Building2,
    iconColor: 'text-emerald-400 bg-emerald-500/10',
    handler: () => { navigate('/clients', 'Clients') },
    permission: 'clients.view',
    staffOnly: true,
    keywords: ['clients', 'organizations', 'companies'],
  },
  {
    id: 'go-vault',
    label: 'Go to Password Vault',
    description: 'Secure credential storage',
    icon: Shield,
    iconColor: 'text-rose-400 bg-rose-500/10',
    handler: () => { navigate('/passwords', 'Password Vault') },
    keywords: ['vault', 'passwords', 'credentials', 'secrets'],
  },
  {
    id: 'toggle-theme',
    label: 'Toggle Dark/Light Mode',
    description: themeStore.theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode',
    icon: themeStore.theme === 'dark' ? Sun : Moon,
    iconColor: 'text-amber-400 bg-amber-500/10',
    handler: () => { themeStore.toggleTheme() },
    keywords: ['theme', 'dark', 'light', 'mode', 'toggle'],
  },
]

const availableActions = computed(() => allActions.filter(a => {
  if (a.staffOnly && !authStore.isStaff) return false
  if (a.permission && !authStore.hasPermission(a.permission)) return false
  return true
}))

// Determine if we're in command mode (query starts with >)
const isCommandMode = computed(() => query.value.trimStart().startsWith('>'))
const commandQuery = computed(() => isCommandMode.value ? query.value.trimStart().slice(1).trim().toLowerCase() : '')

const filteredActions = computed(() => {
  if (isCommandMode.value) {
    if (!commandQuery.value) return availableActions.value
    return availableActions.value.filter(a =>
      a.label.toLowerCase().includes(commandQuery.value) ||
      a.keywords.some(k => k.includes(commandQuery.value))
    )
  }
  // When searching normally, show matching actions at the top
  const q = query.value.trim().toLowerCase()
  if (!q) return availableActions.value.slice(0, 5) // Show top 5 when empty
  return availableActions.value.filter(a =>
    a.label.toLowerCase().includes(q) ||
    a.keywords.some(k => k.includes(q))
  )
})

// --- Search results ---
interface SearchResults {
  tickets?: { id: number; ticket_number: string; subject: string; priority: string; status_name: string; status_color: string; client_name: string }[]
  clients?: { id: number; name: string; status: string }[]
  users?: { id: number; username: string; first_name: string; last_name: string; role: string; client_name: string }[]
  devices?: { device_uid: string; hostname: string; device_type: string; operating_system: string; online_status: string; site_name: string }[]
  projects?: { id: number; name: string; status: string; client_name: string }[]
}

const results = ref<SearchResults>({})

interface FilterOption {
  id: string
  label: string
  icon: Component
  permission?: string
  staffOnly?: boolean
}

const allFilters: FilterOption[] = [
  { id: 'all', label: 'All', icon: Search },
  { id: 'tickets', label: 'Tickets', icon: Ticket, permission: 'tickets.view' },
  { id: 'clients', label: 'Clients', icon: Building2, permission: 'clients.view', staffOnly: true },
  { id: 'users', label: 'Users', icon: Users, permission: 'users.view', staffOnly: true },
  { id: 'devices', label: 'Devices', icon: Monitor, permission: 'datto.view', staffOnly: true },
  { id: 'projects', label: 'Projects', icon: FolderKanban, permission: 'projects.view' },
]

const filters = computed(() => allFilters.filter(f => {
  if (f.staffOnly && !authStore.isStaff) return false
  if (f.permission && !authStore.hasPermission(f.permission)) return false
  return true
}))

// Flatten results into navigable list
interface FlatResult {
  type: string
  section: string
  label: string
  sublabel: string
  icon: Component
  iconColor: string
  route: string
}

const priorityLabels: Record<string, string> = { '1': 'Low', '2': 'Medium', '3': 'High', '4': 'Urgent' }

const flatResults = computed((): FlatResult[] => {
  const items: FlatResult[] = []
  const r = results.value
  const filter = activeFilter.value

  if ((filter === 'all' || filter === 'tickets') && r.tickets?.length) {
    for (const t of r.tickets) {
      items.push({
        type: 'Ticket',
        section: 'Tickets',
        label: `#${t.ticket_number} ${t.subject}`,
        sublabel: [t.client_name, t.status_name, priorityLabels[t.priority]].filter(Boolean).join(' · '),
        icon: Ticket,
        iconColor: 'text-primary-400 bg-primary-500/10',
        route: `/tickets/${t.id}`,
      })
    }
  }

  if ((filter === 'all' || filter === 'clients') && r.clients?.length) {
    for (const c of r.clients) {
      items.push({
        type: 'Client',
        section: 'Clients',
        label: c.name,
        sublabel: c.status || '',
        icon: Building2,
        iconColor: 'text-emerald-400 bg-emerald-500/10',
        route: `/clients?highlight=${c.id}`,
      })
    }
  }

  if ((filter === 'all' || filter === 'users') && r.users?.length) {
    for (const u of r.users) {
      const name = [u.first_name, u.last_name].filter(Boolean).join(' ') || u.username
      items.push({
        type: 'User',
        section: 'Users',
        label: name,
        sublabel: [u.role, u.client_name].filter(Boolean).join(' · '),
        icon: Users,
        iconColor: 'text-violet-400 bg-violet-500/10',
        route: `/users?highlight=${u.id}`,
      })
    }
  }

  if ((filter === 'all' || filter === 'devices') && r.devices?.length) {
    for (const d of r.devices) {
      items.push({
        type: 'Device',
        section: 'Devices',
        label: d.hostname,
        sublabel: [d.device_type, d.operating_system, d.site_name].filter(Boolean).join(' · '),
        icon: Monitor,
        iconColor: d.online_status === 'online' ? 'text-success-400 bg-success-500/10' : 'text-surface-400 bg-surface-500/10',
        route: `/datto-rmm?search=${encodeURIComponent(d.hostname)}`,
      })
    }
  }

  if ((filter === 'all' || filter === 'projects') && r.projects?.length) {
    for (const p of r.projects) {
      items.push({
        type: 'Project',
        section: 'Projects',
        label: p.name,
        sublabel: [p.client_name, p.status].filter(Boolean).join(' · '),
        icon: FolderKanban,
        iconColor: 'text-violet-400 bg-violet-500/10',
        route: `/projects/${p.id}`,
      })
    }
  }

  return items
})

// --- Unified navigable items list ---
interface NavigableItem {
  kind: 'action' | 'recent' | 'result'
  section: string
  label: string
  sublabel: string
  icon: Component
  iconColor: string
  handler: () => void
}

const navigableItems = computed((): NavigableItem[] => {
  const items: NavigableItem[] = []
  const q = query.value.trim()

  // Command mode: only show actions
  if (isCommandMode.value) {
    for (const a of filteredActions.value) {
      items.push({
        kind: 'action',
        section: 'Commands',
        label: a.label,
        sublabel: a.description,
        icon: a.icon,
        iconColor: a.iconColor,
        handler: () => { a.handler(); close() },
      })
    }
    return items
  }

  // Empty query: show recent searches + quick actions
  if (!q) {
    // Recent searches
    for (const r of recentSearches.value) {
      items.push({
        kind: 'recent',
        section: 'Recent',
        label: r.label,
        sublabel: r.type,
        icon: Clock,
        iconColor: 'text-surface-400 bg-surface-500/10',
        handler: () => { navigate(r.route, r.label); close() },
      })
    }
    // Quick actions
    for (const a of filteredActions.value) {
      items.push({
        kind: 'action',
        section: 'Quick Actions',
        label: a.label,
        sublabel: a.description,
        icon: a.icon,
        iconColor: a.iconColor,
        handler: () => { a.handler(); close() },
      })
    }
    return items
  }

  // Normal search: matching actions first, then search results
  if (filteredActions.value.length > 0) {
    for (const a of filteredActions.value.slice(0, 3)) {
      items.push({
        kind: 'action',
        section: 'Actions',
        label: a.label,
        sublabel: a.description,
        icon: a.icon,
        iconColor: a.iconColor,
        handler: () => { a.handler(); close() },
      })
    }
  }

  for (const r of flatResults.value) {
    items.push({
      kind: 'result',
      section: r.section,
      label: r.label,
      sublabel: r.sublabel,
      icon: r.icon,
      iconColor: r.iconColor,
      handler: () => {
        saveRecent({ query: q, route: r.route, label: r.label, type: r.type })
        navigate(r.route, r.label)
        close()
      },
    })
  }

  return items
})

const hasSearched = computed(() => query.value.trim().length >= 2 && !searching.value && !isCommandMode.value)

// Debounced search
let searchTimeout: ReturnType<typeof setTimeout> | null = null

watch(query, (val) => {
  if (searchTimeout) clearTimeout(searchTimeout)

  // Don't search in command mode
  if (val.trimStart().startsWith('>')) {
    results.value = {}
    selectedIndex.value = 0
    return
  }

  if (val.trim().length < 2) {
    results.value = {}
    selectedIndex.value = 0
    return
  }

  searching.value = true
  searchTimeout = setTimeout(() => performSearch(), 250)
})

watch(activeFilter, () => {
  selectedIndex.value = 0
  if (query.value.trim().length >= 2 && !isCommandMode.value) {
    searching.value = true
    performSearch()
  }
})

async function performSearch() {
  try {
    const typesParam = activeFilter.value === 'all' ? '' : `&types=${activeFilter.value}`
    const response = await apiGet<{ results: SearchResults }>(`/search?q=${encodeURIComponent(query.value.trim())}${typesParam}`)
    results.value = response.results || {}
    selectedIndex.value = 0
  } catch (error) {
    logger.error('Global search failed:', error)
    results.value = {}
  } finally {
    searching.value = false
  }
}

function handleKeydown(e: KeyboardEvent) {
  if (e.key === 'ArrowDown') {
    e.preventDefault()
    selectedIndex.value = Math.min(selectedIndex.value + 1, navigableItems.value.length - 1)
    scrollSelectedIntoView()
  } else if (e.key === 'ArrowUp') {
    e.preventDefault()
    selectedIndex.value = Math.max(selectedIndex.value - 1, 0)
    scrollSelectedIntoView()
  } else if (e.key === 'Enter' && navigableItems.value.length > 0) {
    e.preventDefault()
    navigableItems.value[selectedIndex.value]?.handler()
  } else if (e.key === 'Escape') {
    e.preventDefault()
    close()
  } else if (e.key === 'Backspace' && query.value === '' && recentSearches.value.length > 0) {
    // Optional: could clear recent on double backspace
  }
}

function scrollSelectedIntoView() {
  nextTick(() => {
    const el = document.querySelector(`[data-search-index="${selectedIndex.value}"]`)
    el?.scrollIntoView({ block: 'nearest' })
  })
}

function close() {
  query.value = ''
  results.value = {}
  activeFilter.value = 'all'
  selectedIndex.value = 0
  emit('close')
}

// Focus input when modal opens
watch(() => props.show, (val) => {
  if (val) {
    nextTick(() => inputRef.value?.focus())
  }
})

// Prevent body scroll when open
watch(() => props.show, (val) => {
  document.body.style.overflow = val ? 'hidden' : ''
})

// Cleanup
onUnmounted(() => {
  document.body.style.overflow = ''
  if (searchTimeout) clearTimeout(searchTimeout)
})

const isMac = typeof navigator !== 'undefined' && navigator.platform.toUpperCase().includes('MAC')
const modKey = isMac ? '\u2318' : 'Ctrl'
</script>

<template>
  <Teleport to="body">
    <Transition name="search-modal">
      <div
        v-if="show"
        class="fixed inset-0 z-50 flex items-start justify-center pt-[8vh] sm:pt-[15vh] px-3 sm:px-0"
        @click.self="close"
      >
        <!-- Backdrop -->
        <div class="absolute inset-0 bg-black/60 backdrop-blur-sm" @click="close" />

        <!-- Modal -->
        <div
          class="relative w-full max-w-2xl bg-surface-850 border border-surface-700 rounded-xl shadow-2xl overflow-hidden"
          @keydown="handleKeydown"
        >
          <!-- Search Input -->
          <div class="flex items-center gap-3 px-4 py-3 border-b border-surface-700">
            <div v-if="isCommandMode" class="flex items-center justify-center w-5 h-5 flex-shrink-0">
              <Zap class="w-4 h-4 text-amber-400" />
            </div>
            <Search v-else class="w-5 h-5 text-surface-400 flex-shrink-0" />
            <input
              ref="inputRef"
              v-model="query"
              type="text"
              :placeholder="isCommandMode ? 'Type a command...' : 'Search or type > for commands...'"
              class="flex-1 bg-transparent text-surface-100 text-base placeholder-surface-500 focus:outline-none"
              autocomplete="off"
              spellcheck="false"
            />
            <Loader2 v-if="searching" class="w-5 h-5 text-primary-400 animate-spin flex-shrink-0" />
            <button
              v-else-if="query"
              @click="query = ''; results = {}"
              class="p-1 text-surface-500 hover:text-surface-200 rounded transition-colors"
            >
              <X class="w-4 h-4" />
            </button>
            <kbd v-else class="hidden sm:inline-flex items-center justify-center h-5 px-1.5 text-[10px] font-medium text-surface-500 bg-surface-700 border border-surface-600 rounded">
              {{ modKey }}K
            </kbd>
          </div>

          <!-- Category Chips (only show for search mode, not command mode) -->
          <div v-if="!isCommandMode && query.trim().length >= 2" class="flex items-center gap-2 px-4 py-2.5 border-b border-surface-700 overflow-x-auto scrollbar-hide">
            <button
              v-for="filter in filters"
              :key="filter.id"
              @click="activeFilter = filter.id"
              class="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors whitespace-nowrap"
              :class="activeFilter === filter.id
                ? 'bg-primary-500/20 text-primary-300 border border-primary-500/30'
                : 'text-surface-400 hover:text-surface-200 hover:bg-surface-700 border border-transparent'"
            >
              <component :is="filter.icon" class="w-3.5 h-3.5" />
              {{ filter.label }}
            </button>
          </div>

          <!-- Results -->
          <div class="max-h-[50vh] overflow-y-auto">
            <!-- Loading -->
            <div v-if="searching && flatResults.length === 0" class="flex items-center justify-center py-12">
              <Loader2 class="w-6 h-6 animate-spin text-primary-400" />
            </div>

            <!-- No results for search -->
            <div v-else-if="hasSearched && navigableItems.length === 0" class="text-center py-12 text-surface-500 text-sm">
              No results found
            </div>

            <!-- No commands match -->
            <div v-else-if="isCommandMode && filteredActions.length === 0" class="text-center py-12 text-surface-500 text-sm">
              No matching commands
            </div>

            <!-- Navigable items -->
            <div v-else-if="navigableItems.length > 0" class="py-1">
              <template v-for="(item, i) in navigableItems" :key="`${item.kind}-${i}`">
                <!-- Section header when section changes -->
                <div
                  v-if="i === 0 || item.section !== navigableItems[i - 1].section"
                  class="flex items-center gap-2 px-4 pt-3 pb-1.5"
                >
                  <span class="text-[11px] font-semibold uppercase tracking-wider text-surface-500">
                    {{ item.section }}
                  </span>
                  <button
                    v-if="item.section === 'Recent'"
                    @click.stop="clearRecent"
                    class="text-[10px] text-surface-600 hover:text-surface-400 transition-colors"
                  >Clear</button>
                </div>
                <button
                  :data-search-index="i"
                  @click="item.handler()"
                  @mouseenter="selectedIndex = i"
                  class="w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors"
                  :class="selectedIndex === i ? 'bg-surface-700' : 'hover:bg-surface-800'"
                >
                  <div class="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" :class="item.iconColor">
                    <component :is="item.icon" class="w-4 h-4" />
                  </div>
                  <div class="flex-1 min-w-0">
                    <div class="text-sm text-surface-100 truncate">{{ item.label }}</div>
                    <div v-if="item.sublabel" class="text-xs text-surface-500 truncate">{{ item.sublabel }}</div>
                  </div>
                  <ChevronRight v-if="item.kind === 'action'" class="w-3.5 h-3.5 text-surface-600 flex-shrink-0" />
                  <span v-else class="text-[10px] text-surface-600 font-medium uppercase flex-shrink-0">{{ item.section }}</span>
                </button>
              </template>
            </div>

            <!-- Empty state (initial) -->
            <div v-else class="text-center py-12 text-surface-500 text-sm">
              <p>Search across tickets, clients, projects, and more</p>
              <p class="mt-1 text-surface-600 text-xs">Type <kbd class="px-1 py-0.5 bg-surface-700 rounded text-surface-400">></kbd> for commands</p>
            </div>
          </div>

          <!-- Footer -->
          <div class="flex items-center justify-between gap-4 px-4 py-2.5 border-t border-surface-700 bg-surface-800/50">
            <div class="flex items-center gap-1 text-[10px] text-surface-600">
              <Zap class="w-3 h-3" />
              <span>Command Palette</span>
            </div>
            <div class="flex items-center gap-4">
              <div class="flex items-center gap-1.5 text-xs text-surface-500">
                <div class="flex items-center gap-0.5">
                  <kbd class="inline-flex items-center justify-center w-5 h-5 text-[10px] font-medium bg-surface-700 border border-surface-600 rounded">
                    <ArrowUp class="w-3 h-3" />
                  </kbd>
                  <kbd class="inline-flex items-center justify-center w-5 h-5 text-[10px] font-medium bg-surface-700 border border-surface-600 rounded">
                    <ArrowDown class="w-3 h-3" />
                  </kbd>
                </div>
                <span>Navigate</span>
              </div>
              <div class="flex items-center gap-1.5 text-xs text-surface-500">
                <kbd class="inline-flex items-center justify-center w-5 h-5 text-[10px] font-medium bg-surface-700 border border-surface-600 rounded">
                  <CornerDownLeft class="w-3 h-3" />
                </kbd>
                <span>Select</span>
              </div>
              <div class="flex items-center gap-1.5 text-xs text-surface-500">
                <kbd class="inline-flex items-center justify-center min-w-[20px] h-5 px-1 text-[10px] font-medium bg-surface-700 border border-surface-600 rounded">Esc</kbd>
                <span>Close</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.search-modal-enter-active {
  transition: opacity 0.15s ease;
}
.search-modal-leave-active {
  transition: opacity 0.1s ease;
}
.search-modal-enter-from,
.search-modal-leave-to {
  opacity: 0;
}
</style>
